
import { useEffect, useState } from "react";
import { Box, Button, Card, CardContent, CircularProgress, Stack, Typography, Link, Divider } from "@mui/material";
import { bridgeStripeApi } from "./bridgeStripeApi";
import { normalizeCustomer, normalizeKyc } from "./bridgeStripeNormalize";

export default function BridgeStripeGate({ onReady }) {
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [kyc, setKyc] = useState(null);
  const [err, setErr] = useState(null);

  const refreshCustomer = async () => {
    setErr(null);
    const res = await bridgeStripeApi.getCustomer();
    if (!res.ok) {
      setErr(res);
      return;
    }
    setCustomer(normalizeCustomer(res.data));
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await refreshCustomer();
      setLoading(false);
    })();
   
  }, []);

  const createCustomer = async () => {
    setErr(null);
    setLoading(true);
    const res = await bridgeStripeApi.createCustomer();
    setLoading(false);

    if (!res.ok) {
      setErr(res);
      return;
    }

    // After create, fetch full customer object so we get endorsements/capabilities
    await refreshCustomer();
  };

  const getKycLinks = async () => {
    setErr(null);
    const res = await bridgeStripeApi.getKyc();
    if (!res.ok) {
      setErr(res);
      return;
    }
    setKyc(normalizeKyc(res.data));
  };

  // Determine readiness: for now, you can treat "HasAcceptedTermsOfService + base approved" as ready.
  // You may tighten this later depending on what transfers you want enabled.
  const baseEndorsement = customer?.endorsements?.find((e) => e.Name === "base");
  const baseApproved = baseEndorsement?.Status === "approved";
  const tosOk = customer?.hasAcceptedTos === true;

  useEffect(() => {
    if (customer?.id && tosOk && baseApproved) {
      onReady?.(customer);
    }
  }, [customer, tosOk, baseApproved, onReady]);

  if (loading) {
    return (
      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CircularProgress size={18} />
            <Typography>Loading Bridge customer…</Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ borderRadius: 3 }}>
      <CardContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
          Bridge onboarding
        </Typography>

        {err ? (
          <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, bgcolor: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)" }}>
            <Typography sx={{ fontWeight: 700 }}>Error</Typography>
            <Typography variant="body2">{String(err.error || "Unknown error")}</Typography>
          </Box>
        ) : null}

        {!customer ? (
          <Stack spacing={1.5}>
            <Typography variant="body2" sx={{ color: "#6B7280" }}>
              No Bridge customer exists for this session yet.
            </Typography>
            <Button variant="contained" onClick={createCustomer}>
              Create customer
            </Button>
          </Stack>
        ) : (
          <Stack spacing={1.25}>
            <Typography variant="body2">
              Customer: <b>{customer.id}</b>
            </Typography>

            <Divider />

            <Typography variant="body2">
              Terms accepted: <b>{tosOk ? "Yes" : "No"}</b>
            </Typography>
            <Typography variant="body2">
              Base endorsement: <b>{baseApproved ? "Approved" : (baseEndorsement?.Status || "Unknown")}</b>
            </Typography>

            {customer?.requirementsDue?.length ? (
              <Typography variant="body2" sx={{ color: "#6B7280" }}>
                Requirements due: {customer.requirementsDue.join(", ")}
              </Typography>
            ) : null}

            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Button variant="outlined" onClick={refreshCustomer}>
                Refresh
              </Button>
              <Button variant="outlined" onClick={getKycLinks}>
                Get KYC + ToS links
              </Button>
            </Stack>

            {kyc?.kycUrl || kyc?.tosUrl ? (
              <Stack spacing={0.5} sx={{ mt: 1 }}>
                {kyc.kycUrl ? (
                  <Typography variant="body2">
                    KYC:{" "}
                    <Link href={kyc.kycUrl} target="_blank" rel="noreferrer">
                      Open verification
                    </Link>
                  </Typography>
                ) : null}
                {kyc.tosUrl ? (
                  <Typography variant="body2">
                    ToS:{" "}
                    <Link href={kyc.tosUrl} target="_blank" rel="noreferrer">
                      Accept terms
                    </Link>
                  </Typography>
                ) : null}
              </Stack>
            ) : null}

            {tosOk && baseApproved ? (
              <Box sx={{ mt: 1, p: 1.25, borderRadius: 2, bgcolor: "rgba(22,163,74,0.08)", border: "1px solid rgba(22,163,74,0.25)" }}>
                <Typography sx={{ fontWeight: 700 }}>Ready</Typography>
                <Typography variant="body2" sx={{ color: "#065F46" }}>
                  Customer is onboarded and can proceed.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ mt: 1, p: 1.25, borderRadius: 2, bgcolor: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.25)" }}>
                <Typography sx={{ fontWeight: 700 }}>Not ready</Typography>
                <Typography variant="body2" sx={{ color: "#92400E" }}>
                  Complete KYC + accept ToS, then refresh to see approvals.
                </Typography>
              </Box>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
